import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Col, Label, Row, Tooltip } from 'reactstrap';
import BZAPIService from '../../../services/BZAPIService';
import Select from '../../common/Select';
import { isEmpty } from 'lodash';

// Format date to YYYY-MM-DD HH:mm:ss
const formatDate = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Warning triangle icon component
const WarningIcon = ({ id, tooltip, isOpen, onToggle }) => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="16"
      width="16"
      viewBox="0 0 512 512"
      id={id}
      style={{ cursor: 'pointer' }}
    >
      <path
        fill="#bd472a"
        d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"
      />
    </svg>
    {tooltip && (
      <Tooltip placement="top" isOpen={isOpen} target={id} autohide={true} toggle={onToggle}>
        {tooltip}
      </Tooltip>
    )}
  </>
);

WarningIcon.propTypes = {
  id: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func
};

// Table header columns
const TABLE_COLUMNS = [
  { key: 'endedAt', label: 'Ended' },
  { key: 'errors', label: 'Errors' },
  { key: 'compatible', label: 'Compatible' },
  { key: 'incompatible', label: 'Incompatible' },
  { key: 'bugsFiltered', label: 'Bugs filtered' },
  { key: 'bugsInserted', label: 'Bugs inserted' },
  { key: 'managedProducts', label: 'Managed products' },
  { key: 'syncOnly', label: 'Sync only' }
];

// Filter options for the dropdown
const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Bugs inserted', value: 'bugsinserted' },
  { label: 'Bugs updated', value: 'bugsupdated' },
  { label: 'Errors', value: 'errors' }
];

export default function HistoryTab({ vendorId }) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [historyType, setHistoryType] = useState({ label: 'All', value: 'all' });
  const [isOpenTooltip, setIsOpenTooltip] = useState({});

  async function getHistory(e) {
    const selectedType = e?.value?.length ? e : historyType;
    setHistoryType(selectedType);
    try {
      const res = await BZAPIService.getBugServiceHistory(vendorId, selectedType.value);
      const filteredHistory =
        res?.data?.results?.filter(h => {
          return h?.vendorData.configuration_completed === true;
        }) || [];
      setHistory(filteredHistory);
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setHistory([]);
    setIsLoading(true);
    setHistoryType({ label: 'All', value: 'all' });
    getHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const toggleTooltip = (e, id) => {
    setIsOpenTooltip(e.type === 'mouseover' ? id : '');
  };

  // Get value for a specific column from history entry
  const getCellValue = (entry, columnKey) => {
    if (columnKey === 'endedAt') {
      return formatDate(entry.endedAt);
    }

    const vendorData = entry.vendorData || {};

    // Map column keys to vendorData fields
    const fieldMap = {
      errors: 'errors',
      compatible: 'compatibleCis',
      incompatible: 'incompatibleCis',
      bugsFiltered: 'bugsFiltered',
      bugsInserted: 'bugsInserted',
      managedProducts: 'discoveredManagedProducts',
      syncOnly: 'syncOnly'
    };

    const field = fieldMap[columnKey];
    const value = vendorData[field];

    // Handle errors array - show warning icon if there are errors
    if (columnKey === 'errors') {
      if (Array.isArray(value) && value.length > 0) {
        const errorMessage = value[0]?.value || 'Error occurred';
        return (
          <WarningIcon
            id={`error-${entry.id}`}
            tooltip={errorMessage}
            isOpen={isOpenTooltip === `error-${entry.id}`}
            onToggle={e => toggleTooltip(e, `error-${entry.id}`)}
          />
        );
      }
      return '-';
    }

    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle null/undefined/0 values
    if (value === null || value === undefined || value === 0) {
      return '-';
    }

    return value;
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem'
  };

  const headerStyle = {
    borderBottom: '1px solid #d8e2ef',
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#5e6e82',
    whiteSpace: 'nowrap'
  };

  const cellStyle = {
    borderBottom: '1px solid #edf2f9',
    padding: '12px 16px',
    color: '#344050'
  };

  return (
    <Card className="mb-4">
      <CardBody>
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2rem', height: '2rem' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <p className="text-muted mb-0">Loading history...</p>
          </div>
        ) : (
          <div>
            <h5 style={{ fontWeight: 600, color: '#344050', marginBottom: '8px' }}>Bug Service Execution History</h5>
            <p style={{ color: '#748194', marginBottom: '24px' }}>Displaying up to the last 10 executions</p>

            <Row className="mb-4">
              <Col sm="4">
                <Label style={{ fontWeight: 500, color: '#5e6e82', marginBottom: '8px' }}>
                  Choose data to display:
                </Label>
                <Select options={FILTER_OPTIONS} value={historyType} onChange={e => getHistory(e)} />
              </Col>
            </Row>

            {!isEmpty(history) ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      {TABLE_COLUMNS.map(col => (
                        <th key={col.key} style={headerStyle}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry, index) => (
                      <tr key={entry.id || index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f0efec' }}>
                        {TABLE_COLUMNS.map(col => (
                          <td key={col.key} style={cellStyle}>
                            {getCellValue(entry, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-4">
                <strong>No execution logs found</strong>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

HistoryTab.propTypes = {
  vendorId: PropTypes.string
};
